import Typography from "@mui/joy/Typography";
import { MilliDelta } from "../../../../../../models/common";
import {
  ApproximateTime,
  ExactTime,
  QualitativeTimeDelta,
  QuantitativeTimeDelta,
  RelativeTime,
  TemporalPrecision,
  TimeInterval,
} from "../../../../../../models/human_time";
import {
  isApproximateTime,
  isRelativeTime,
  isTimeInterval,
} from "../../../../../../shared/utils/fact";
import { timeToDayjs } from "../../../../../../shared/utils/time";

function formatDependingOnPrecision(date: ApproximateTime | ExactTime | RelativeTime): string {
  if (!isApproximateTime(date)) {
    return "DD MMM YYYY";
  }

  const precision = date.precision;
  if (precision === TemporalPrecision.YEAR) {
    return "YYYY";
  }
  if (precision === TemporalPrecision.MONTH) {
    return "MMM YYYY";
  }
  return "DD MMM YYYY";
}

// Mapping of temporal precision to unit names
const TEMPORAL_PRECISION_UNITS = {
  [TemporalPrecision.YEAR]: "year",
  [TemporalPrecision.MONTH]: "month",
  [TemporalPrecision.WEEK]: "week",
  [TemporalPrecision.DAY]: "day",
  [TemporalPrecision.HOUR]: "hour",
  [TemporalPrecision.MINUTE]: "minute",
  [TemporalPrecision.SECOND]: "second",
  [TemporalPrecision.MILLI]: "millisecond",
} as const;

// Helper function to pluralize unit names
const pluralizeUnit = (unit: string, delta: MilliDelta): string => {
  return delta === 1 ? unit : `${unit}s`;
};

function temporalPrecisionNiceName(delta: MilliDelta, precision: TemporalPrecision): string {
  const unit = TEMPORAL_PRECISION_UNITS[precision];
  return unit ? pluralizeUnit(unit, delta) : "Unknown";
}

function computeRelativeTimeDisplay(time: RelativeTime): string {
  if (typeof time.delta === "string") {
    return time.delta;
  } else {
    // then here it's quantitative
    const delta = time.delta;
    const precision = delta.precision;
    const value = delta.value;
    if (value < 0) {
      return `${value * -1} ${temporalPrecisionNiceName(
        (value * -1) as MilliDelta,
        precision
      )} ago`;
    } else if (value > 0) {
      return `in ${value} ${temporalPrecisionNiceName(value as MilliDelta, precision)}`;
    } else {
      return "Present";
    }
  }
}

// Helper function to format date based on whether it's relative or not
const formatDateForInterval = (date: ApproximateTime | ExactTime | RelativeTime): string => {
  return isRelativeTime(date)
    ? computeRelativeTimeDisplay(date)
    : timeToDayjs(date)!.format(formatDependingOnPrecision(date));
};

// Helper function to render time interval display
const renderTimeInterval = (factDate: TimeInterval): JSX.Element => {
  const startDate = formatDateForInterval(factDate.begin);
  const endDate = formatDateForInterval(factDate.end);

  return (
    <Typography data-cy="fact-paper-date-display" level="body-sm">
      {startDate} — {endDate}
    </Typography>
  );
};

// Helper function to render quantitative relative time
const renderQuantitativeRelativeTime = (factDate: RelativeTime): JSX.Element => {
  const delta = factDate.delta as QuantitativeTimeDelta;

  if (delta.value === 0) {
    return (
      <Typography data-cy="fact-paper-date-display" level="body-sm">
        Present
      </Typography>
    );
  }

  return (
    <Typography data-cy="fact-paper-date-display" level="body-sm">
      {computeRelativeTimeDisplay(factDate)}
    </Typography>
  );
};

// Helper function to render qualitative relative time
const renderQualitativeRelativeTime = (factDate: RelativeTime): JSX.Element => {
  const isPast = factDate.delta === QualitativeTimeDelta.BEFORE;

  return (
    <div>
      <Typography data-cy="fact-paper-date-display" level="body-sm">
        {isPast ? "Past" : "Future"}
      </Typography>
    </div>
  );
};

// Helper function to render relative time display
const renderRelativeTime = (factDate: RelativeTime): JSX.Element => {
  const isQuantitative = typeof factDate.delta === "object";

  return (
    <>
      {isQuantitative
        ? renderQuantitativeRelativeTime(factDate)
        : renderQualitativeRelativeTime(factDate)}
    </>
  );
};

// Helper function to render exact/approximate time
const renderExactTime = (factDate: ApproximateTime | ExactTime): JSX.Element => {
  const date = timeToDayjs(factDate);

  return (
    <Typography data-cy="fact-paper-date-display" level="body-sm">
      {date?.format(formatDependingOnPrecision(factDate))}
    </Typography>
  );
};

export default function FactPaperDateDisplay({
  factDate,
}: {
  factDate?: ApproximateTime | ExactTime | RelativeTime | TimeInterval | null;
}) {
  if (!factDate) {
    return null;
  }

  if (isTimeInterval(factDate)) {
    return renderTimeInterval(factDate);
  }

  if (isRelativeTime(factDate)) {
    return renderRelativeTime(factDate);
  }

  return renderExactTime(factDate);
}
